import { useState } from "react";
import { X, Phone, CalendarDays, Wallet, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useApp } from "@/context/AppContext";
import { fmtDate, fmtNum } from "@/lib/format";
import { FileImage } from "@/components/files/FileFields";
import { staffEvents, staffDebt, attendance, clockStats, seniority, today, onLeave } from "@/lib/hr";
import StaffProfileForm from "./StaffProfileForm";
import Payroll from "./Payroll";
import Leave from "./Leave";
import HrEventList from "./HrEventList";

const Badge = ({ icon: Icon, label, value, testid }) => (
  <div className="flex items-center gap-2 rounded border border-[#CCCCCC] bg-white px-3 h-12 min-w-0">
    <Icon className="w-4 h-4 text-[#2B79C2] shrink-0" /><div className="min-w-0"><div className="text-[10px] text-[#666] leading-none">{label}</div><div className="font-bold truncate text-sm" data-testid={testid}>{value}</div></div>
  </div>
);

export default function StaffProfile({ member, onClose }) {
  const { t, view, money, lang } = useApp();
  const [tab, setTab] = useState("profile");
  if (!member) return null;
  const isNew = !member.id;
  const evs = isNew ? [] : staffEvents(view.hr_events, member.id);
  const debt = staffDebt(evs);
  const att = attendance(view.shifts, member.name, today().slice(0, 7));
  const ck = clockStats(view.attendance, member.id, today().slice(0, 7));
  const sen = seniority(member.hire_date);
  const leave = onLeave(evs, today());
  return (
    <Dialog open={!!member} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="dlg-full p-0 bg-[#F0F0F0]" data-testid="staff-dialog">
        <div className="max-w-6xl mx-auto w-full p-4 space-y-4">
          <div className="flex items-start gap-4">
            <div className="w-20 h-20 rounded bg-[#2B79C2] text-white grid place-items-center text-3xl font-black overflow-hidden shrink-0">
              {member.photo_id ? <FileImage id={member.photo_id} className="w-full h-full object-cover" alt={member.name} /> : (member.name || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="font-heading text-2xl truncate" data-testid="staff-dialog-title">{isNew ? t("add_staff") : member.name}</DialogTitle>
              {!isNew && <div className="text-sm text-[#555]">{t(`role_${member.role}`)}{!member.active && ` · ${t("inactive")}`}{leave && <span className="ms-2 px-2 py-0.5 rounded bg-[#C7353B] text-white text-xs font-bold" data-testid="staff-on-leave">{t("hr_on_leave_today")} · {t(`hr_kind_${leave.kind}`)}</span>}</div>}
            </div>
            <button type="button" onClick={onClose} className="w-10 h-10 rounded border border-[#ADADAD] bg-white grid place-items-center hover:bg-[#FDE8E8]" data-testid="staff-dialog-close"><X className="w-5 h-5" /></button>
          </div>
          {!isNew && (
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
              <Badge icon={Phone} label={t("hr_phone")} value={member.phone || "—"} />
              <Badge icon={Wallet} label={t("hr_salary")} value={member.salary ? `${money(member.salary)} · ${t(`hr_${member.salary_type || "monthly"}`)}` : "—"} testid="staff-badge-salary" />
              <Badge icon={Wallet} label={t("hr_debt")} value={money(debt)} testid="staff-badge-debt" />
              <Badge icon={CalendarDays} label={t("hr_seniority")} value={sen ? (sen.years != null ? t("hr_years", { n: sen.years }) : t("hr_months", { n: sen.months })) : "—"} />
              <Badge icon={Clock} label={t("hr_shifts_month")} value={`${att.count} · ${fmtNum(att.hours)}h`} testid="staff-badge-shifts" />
              {ck.open && <span className="col-span-2 lg:col-span-5 text-xs font-bold text-[#23714A]" data-testid="staff-clocked-now">● {t("clocked_in_now")} · {fmtDate(ck.open.in_at, lang)}</span>}
            </div>
          )}
          {isNew ? <StaffProfileForm member={member} onSaved={onClose} /> : (
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="w-full h-11 mb-3">
                {["profile", "payroll", "leave", "attendance", "history"].map((k) => <TabsTrigger key={k} value={k} className="flex-1 h-9 font-bold" data-testid={`staff-tab-${k}`}>{t(`hr_${k}`)}</TabsTrigger>)}
              </TabsList>
              <TabsContent value="profile"><StaffProfileForm key={member.updated_at} member={member} /></TabsContent>
              <TabsContent value="payroll"><Payroll member={member} events={evs} /></TabsContent>
              <TabsContent value="leave"><Leave member={member} events={evs} /></TabsContent>
              <TabsContent value="attendance">
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 mb-3">
                  <Badge icon={Clock} label={`${t("clock_days")} · ${t("hr_month")}`} value={ck.days} testid="hr-clock-days" />
                  <Badge icon={Clock} label={`${t("clock_hours")} · ${t("hr_month")}`} value={`${fmtNum(ck.hours)} h`} testid="hr-clock-hours" />
                  <Badge icon={Clock} label={t("hr_shifts_month")} value={att.count} />
                  <Badge icon={Clock} label={t("hr_hours_month")} value={`${fmtNum(att.hours)} h`} />
                  <Badge icon={CalendarDays} label={t("hr_last_shift")} value={att.last ? fmtDate(att.last.opened_at, lang) : "—"} />
                </div>
                <div className="font-bold text-sm mb-1">{t("clock_records")}</div>
                <div className="space-y-1 mb-4" data-testid="hr-clock-list">
                  {ck.all.length === 0 && <p className="text-sm text-muted-foreground">{t("hr_no_events")}</p>}
                  {ck.all.slice(0, 60).map((a) => (
                    <div key={a.id} className={`rounded border px-3 py-2 text-sm flex justify-between ${a.out_at ? "border-[#E5E5E5] bg-white" : "border-[#9CD3AE] bg-[#E6F4EA]"}`} data-testid="hr-clock-row">
                      <span>{fmtDate(a.in_at, lang)} → {a.out_at ? fmtDate(a.out_at, lang) : <b className="text-[#23714A]">{t("clocked_in_now")}</b>}</span>
                      <span dir="ltr" className="font-bold">{fmtNum(ck.hrs(a))} h</span>
                    </div>
                  ))}
                </div>
                <div className="font-bold text-sm mb-1">{t("shift")}</div>
                <div className="space-y-1" data-testid="hr-attendance-list">
                  {Object.values(view.shifts).filter((s) => s.opened_by === member.name).sort((a, b) => b.opened_at.localeCompare(a.opened_at)).slice(0, 60).map((s) => (
                    <div key={s.id} className="rounded border border-[#E5E5E5] bg-white px-3 py-2 text-sm flex justify-between"><span>{fmtDate(s.opened_at, lang)} → {s.closed_at ? fmtDate(s.closed_at, lang) : t("open_shift")}</span><span dir="ltr" className="font-bold">{s.closed_at ? `${fmtNum((new Date(s.closed_at) - new Date(s.opened_at)) / 3600000)} h` : ""}</span></div>
                  ))}
                </div>
              </TabsContent>
              <TabsContent value="history"><HrEventList events={evs} member={member} /></TabsContent>
            </Tabs>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
